import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Eye, Archive, ArchiveRestore, Trash2, Edit3, Save } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import PdfUpload from "@/components/PdfUpload";
import DiffHistory from "@/components/DiffHistory";
import MarkdownEditor from "@/components/MarkdownEditor";
import PublicLinkButton from "@/components/PublicLinkButton";
import { useAdminTable } from "@/lib/useAdminTable";
import { AdminTableToolbar, AdminTablePager } from "@/components/admin/AdminTableHelpers";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";

const CATS = ["Innovation", "Technologie", "Startup", "Marché", "Digital", "Communiqué", "Événements", "Interviews"];

export default function AdminNews() {
  const table = useAdminTable("/admin/news", { sort: "created_at", order: "desc" });
  const news = table.items;
  const list = news;
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", category: "Innovation", image: "", tags: "", publish_at: "", pdf_url: "", pdf_filename: "", pdf_thumbnail: "", lead_capture_enabled: false, source_lang: "fr" });

  const load = () => table.reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {}, []);

  const openEdit = (n) => {
    setEditing({
      id: n.id,
      title: n.title || "",
      excerpt: n.excerpt || "",
      body: n.body || "",
      category: n.category || "Innovation",
      image: n.image || "",
      tags: (n.tags || []).join(", "),
      status: n.status || "draft",
      pdf_url: n.pdf_url || "",
      pdf_filename: n.pdf_filename || "",
      pdf_thumbnail: n.pdf_thumbnail || "",
      lead_capture_enabled: !!n.lead_capture_enabled,
      download_count: n.download_count || 0,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const tags = editing.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.patch(`/news/${editing.id}`, {
        title: editing.title, excerpt: editing.excerpt, body: editing.body,
        category: editing.category, image: editing.image, tags, status: editing.status,
        pdf_url: editing.pdf_url, pdf_filename: editing.pdf_filename,
        pdf_thumbnail: editing.pdf_thumbnail,
        lead_capture_enabled: editing.lead_capture_enabled,
      });
      toast.success("Actualité modifiée");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erreur");
    }
  };

  const upd = async (id, status) => {
    try { await api.patch(`/news/${id}`, { status }); toast.success("OK"); load(); } catch { toast.error("Erreur"); }
  };
  const archiveNews = async (n) => {
    if (!window.confirm(`Archiver "${n.title}" ?`)) return;
    await api.post(`/admin/news/${n.id}/archive`); toast.success("Archivée"); load();
  };
  const unarchiveNews = async (n) => {
    await api.post(`/admin/news/${n.id}/unarchive`); toast.success("Restaurée"); load();
  };
  const deleteNews = async (n) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT "${n.title}" ?\nCette action est IRRÉVERSIBLE.`)) return;
    await api.delete(`/admin/news/${n.id}`); toast.success("Supprimée"); load();
  };

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => { const ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  const selectAll = () => setSelected(new Set(list.map((n) => n.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${selected.size} actualité${selected.size > 1 ? "s" : ""} ?\nCette action est IRRÉVERSIBLE.`)) return;
    try {
      const r = await api.post("/admin/news/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} actualité${r.data.deleted > 1 ? "s" : ""} supprimée${r.data.deleted > 1 ? "s" : ""}`);
      clearSel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { ...form, tags };
      if (form.publish_at) payload.publish_at = new Date(form.publish_at).toISOString();
      else delete payload.publish_at;
      await api.post("/news", payload);
      toast.success(form.publish_at ? "Actualité programmée !" : "Actualité publiée !");
      setOpen(false);
      setForm({ title: "", excerpt: "", body: "", category: "Innovation", image: "", tags: "", publish_at: "", pdf_url: "", pdf_filename: "", pdf_thumbnail: "", lead_capture_enabled: false, source_lang: "fr" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Actualités</h1>
          <p className="mt-1 text-neutral-600">Créez, programmez et modérez les news publiées sur le site.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="admin-news-create-btn">
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Fermer" : "Nouvelle actualité"}
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="mt-6 grid gap-4 rounded-2xl border border-wine-200 bg-white p-6 md:grid-cols-2" data-testid="admin-news-form">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="news-create-title" />
          <div><label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Catégorie</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-create-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Programmer publication (optionnel)</label><input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-create-publish-at" /></div>
          {/* Sprint 92ao — Explicit source language → faithful auto-translation */}
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-pink-700">🌍 Langue de rédaction (sera traduit automatiquement dans les autres langues)</label>
            <select value={form.source_lang} onChange={(e) => setForm({ ...form, source_lang: e.target.value })} className="mt-1 w-full rounded-xl border border-pink-200 bg-pink-50/40 px-4 py-2.5 text-sm" data-testid="news-create-source-lang">
              <option value="fr">🇫🇷 Français</option>
              <option value="en">🇬🇧 English</option>
              <option value="es">🇪🇸 Español</option>
              <option value="it">🇮🇹 Italiano</option>
              <option value="pt">🇵🇹 Português</option>
              <option value="de">🇩🇪 Deutsch</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Image de couverture" testId="news-image" />
          </div>
          <textarea required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Extrait (1-2 phrases)" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="news-create-excerpt" />
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Contenu (Markdown)</label>
            <div className="mt-1"><MarkdownEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} rows={8} testId="news-body" /></div>
          </div>
          <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (séparés par virgule)" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="news-create-tags" />
          <div className="md:col-span-2 rounded-2xl border-2 border-dashed border-wine-200 bg-wine-50/20 p-4 space-y-3">
            <div className="text-sm font-bold text-navy">Publication / Livre blanc (optionnel)</div>
            <p className="text-[11px] text-neutral-600">Joindre un PDF transforme l&apos;actualité en publication téléchargeable (avec capture RGPD si activée).</p>
            <PdfUpload
              value={form.pdf_url}
              filename={form.pdf_filename}
              onChange={(v) => setForm({ ...form, pdf_url: v.url || "", pdf_filename: v.filename || "", pdf_thumbnail: v.thumbnail_url || form.pdf_thumbnail })}
              label="PDF du livre blanc / publication"
              testId="news-create-pdf"
            />
            {form.pdf_url && (
              <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700" data-testid="news-create-lead-toggle">
                <input type="checkbox" checked={form.lead_capture_enabled} onChange={(e) => setForm({ ...form, lead_capture_enabled: e.target.checked })} className="h-4 w-4" />
                Activer la capture de leads (formulaire RGPD avant téléchargement)
              </label>
            )}
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="news-create-submit">{form.publish_at ? "Programmer" : "Publier maintenant"}</button>
            <span className="ml-3 text-xs text-neutral-500">L&apos;admin publie directement (pas de validation requise)</span>
          </div>
        </form>
      )}

      <div className="mt-6"><AdminTableToolbar
        table={table}
        placeholder="Rechercher titre, contenu, catégorie…"
        filters={[
          { key: "status", label: "Tous statuts", options: [{ value: "published", label: "Publiée" }, { value: "pending", label: "En attente" }, { value: "rejected", label: "Refusée" }, { value: "draft", label: "Brouillon" }] },
          { key: "category", label: "Toutes catégories", options: CATS.map((c) => ({ value: c, label: c })) },
        ]}
      /></div>

      {selected.size > 0 && (
        <div className="mt-3">
          <BulkActionsBar
            count={selected.size}
            total={list.length}
            onClear={clearSel}
            onSelectAll={selectAll}
            onDelete={bulkDelete}
            label="actualité"
            testId="admin-news-bulk"
          />
        </div>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {list.map((n) => {
          const BACKEND = process.env.REACT_APP_BACKEND_URL;
          const imgSrc = n.image?.startsWith("/api/") ? `${BACKEND}${n.image}` : n.image;
          const scheduled = n.publish_at && n.publish_at > new Date().toISOString();
          const isSelected = selected.has(n.id);
          return (
            <div key={n.id} className={`relative rounded-2xl border bg-white p-5 ${isSelected ? "border-rose-400 ring-2 ring-rose-300" : (n.archived ? "border-amber-300 opacity-70" : "border-neutral-100")}`} data-testid={`admin-news-${n.id}`}>
              <div className="absolute top-3 left-3 z-10">
                <BulkCheckbox checked={isSelected} onChange={() => toggleSel(n.id)} testId={`admin-news-select-${n.id}`} />
              </div>
              {imgSrc && <img src={imgSrc} alt="" className="aspect-[16/9] w-full rounded-xl object-cover" />}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-wine-700">{n.category}</span>
                {scheduled && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Programmé</span>}
                {n.archived && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">ARCHIVÉE</span>}
              </div>
              <h3 className="mt-2 text-base font-bold text-navy">{n.title}</h3>
              <div className="mt-2 text-xs text-neutral-500">par {n.author} · <span className="font-semibold">{n.status}</span></div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => setPreview(n)} className="inline-flex items-center gap-1 rounded-full bg-wine-50 px-3 py-1 text-xs font-semibold text-wine-700 hover:bg-wine-100" data-testid={`admin-news-preview-${n.id}`}><Eye className="h-3 w-3" /> Aperçu</button>
                {n.status === "pending" && (
                  <>
                    <button onClick={() => upd(n.id, "published")} className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100" data-testid={`admin-news-pub-${n.id}`}>Publier</button>
                    <button onClick={() => upd(n.id, "rejected")} className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100" data-testid={`admin-news-rej-${n.id}`}>Refuser</button>
                  </>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {n.status === "published" && !n.archived && <PublicLinkButton slug={n.slug} type="news" compact />}
                  <button onClick={() => openEdit(n)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-wine-50 hover:text-wine-700" title="Modifier" data-testid={`admin-news-edit-${n.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                  {n.archived
                    ? <button onClick={() => unarchiveNews(n)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-emerald-50 hover:text-emerald-700" title="Restaurer" data-testid={`admin-news-unarchive-${n.id}`}><ArchiveRestore className="h-3.5 w-3.5" /></button>
                    : <button onClick={() => archiveNews(n)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-amber-50 hover:text-amber-700" title="Archiver" data-testid={`admin-news-archive-${n.id}`}><Archive className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => deleteNews(n)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`admin-news-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-2xl border border-neutral-100 bg-white"><AdminTablePager table={table} /></div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="news-preview-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setPreview(null)} aria-label="Fermer" />
          <div className="relative max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl">
            <button onClick={() => setPreview(null)} className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid="news-preview-close" aria-label="Fermer"><X className="h-5 w-5" /></button>
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-wine-700">{preview.category}</span>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-700">{preview.status}</span>
            </div>
            <h2 className="text-3xl font-bold text-navy">{preview.title}</h2>
            <div className="mt-2 text-xs text-neutral-500">par {preview.author}</div>
            {preview.image && <img src={preview.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${preview.image}` : preview.image} alt="" className="mt-5 aspect-[16/9] w-full rounded-2xl object-cover" />}
            <p className="mt-5 text-base text-neutral-700">{preview.excerpt}</p>
            <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{preview.body}</div>
            {preview.status === "pending" && (
              <div className="mt-6 flex gap-3 border-t border-neutral-100 pt-5">
                <button onClick={() => { upd(preview.id, "published"); setPreview(null); }} className="rounded-full bg-green-500 px-5 py-2 text-sm font-semibold text-white hover:bg-green-600" data-testid="news-preview-publish">Publier maintenant</button>
                <button onClick={() => { upd(preview.id, "rejected"); setPreview(null); }} className="rounded-full bg-red-500 px-5 py-2 text-sm font-semibold text-white hover:bg-red-600" data-testid="news-preview-reject">Refuser</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit modal — works for any status (validated or not) */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="news-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditing(null)} aria-label="Fermer" />
          <div className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier l&apos;actualité</h2>
              <button onClick={() => setEditing(null)} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Titre" className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-edit-title" />
              <textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} placeholder="Excerpt" className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-edit-excerpt" />
              <MarkdownEditor value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} placeholder="Corps de l'article (Markdown)" data-testid="news-edit-body" />
              <div className="grid gap-3 md:grid-cols-3">
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="news-edit-status">
                  <option value="draft">Brouillon</option>
                  <option value="pending">En attente</option>
                  <option value="published">Publiée</option>
                  <option value="rejected">Refusée</option>
                </select>
                <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="Tags (séparés par virgule)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              </div>
              <ImageUpload value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />

              {/* Publication / livre blanc — RGPD lead capture */}
              <div className="rounded-xl border-2 border-wine-100 bg-wine-50/30 p-4 space-y-3" data-testid="news-publication-block">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-wine-700">📄 Publication / Livre blanc</div>
                    <div className="text-[11px] text-neutral-600">Rattachez un PDF à cette actualité pour la promouvoir dans la section « Publications » de la page Actualités.</div>
                  </div>
                  {editing.download_count > 0 && (
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700">📥 {editing.download_count} téléch.</span>
                  )}
                </div>
                {editing.pdf_url && /example\.(com|org|net)/i.test(editing.pdf_url) && (
                  <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="news-pdf-placeholder-warning">
                    ⚠️ <strong>URL de test détectée</strong> : <code className="rounded bg-white px-1.5 py-0.5">{editing.pdf_url}</code>
                    <br />Cette URL ne pointe pas vers un vrai fichier. Uploadez le vrai PDF ci-dessous pour rendre le lien fonctionnel.
                    <button type="button" onClick={() => setEditing({ ...editing, pdf_url: "", pdf_filename: "", pdf_thumbnail: "" })}
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-900 hover:bg-amber-300"
                            data-testid="news-pdf-clear-placeholder">
                      Effacer l&apos;URL de test
                    </button>
                  </div>
                )}
                <input value={editing.pdf_filename} onChange={(e) => setEditing({ ...editing, pdf_filename: e.target.value })} placeholder="Nom du fichier affiché (ex: Livre Blanc WineTech 2025.pdf)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="news-edit-pdf-filename" />
                <PdfUpload
                  value={editing.pdf_url}
                  filename={editing.pdf_filename}
                  onChange={({ url, filename, thumbnail_url }) => setEditing({
                    ...editing,
                    pdf_url: url,
                    pdf_filename: filename || editing.pdf_filename,
                    pdf_thumbnail: thumbnail_url || editing.pdf_thumbnail || "",
                  })}
                  label="Uploader le PDF (20 MB max)"
                  testId="news-pdf-upload"
                />
                {editing.pdf_thumbnail && (
                  <div className="flex items-start gap-3 mt-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="news-pdf-thumb-preview">
                    <img src={(editing.pdf_thumbnail.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL : "") + editing.pdf_thumbnail}
                         alt="Aperçu PDF" className="h-24 w-auto rounded-lg border border-neutral-200 shadow-sm" />
                    <div className="text-xs text-emerald-800">
                      <strong>✓ Aperçu généré</strong><br/>
                      <span className="text-neutral-600">Page 1 du PDF utilisée comme miniature dans le modal de téléchargement.</span>
                    </div>
                  </div>
                )}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.lead_capture_enabled} onChange={(e) => setEditing({ ...editing, lead_capture_enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-wine-500" data-testid="news-edit-lead-capture" />
                  <span className="text-xs text-neutral-700">
                    <strong>Activer la capture de leads RGPD</strong> — l&apos;utilisateur devra remplir un formulaire (prénom, nom, email, entreprise + consentement) avant d&apos;obtenir le PDF. Les leads alimentent automatiquement la page <em>Contacts</em>.
                  </span>
                </label>
                {editing.id && editing.pdf_url && (
                  <a href={`/admin/news`} onClick={(e) => { e.preventDefault(); window.open(`/admin/publications/${editing.id}/leads`, "_blank"); }}
                     className="inline-flex items-center gap-1 text-[11px] font-bold text-wine-600 hover:underline" data-testid="news-view-leads">
                    👥 Voir les leads collectés →
                  </a>
                )}
              </div>
            </div>

            {/* Diff log */}
            <details className="mt-5 rounded-xl border border-neutral-100 bg-neutral-50 p-3" data-testid="news-diff-history-section">
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-neutral-600">Historique des modifications</summary>
              <div className="mt-3">
                <DiffHistory entityType="news" entityId={editing.id} />
              </div>
            </details>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveEdit} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="news-edit-save"><Save className="h-3.5 w-3.5" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
